import React, { useState,useEffect } from "react";
import {
    Box,
    Grid,
    Typography,
    Button,
    Card,
    Divider,
    Chip,
    Stack,
    TextField,
    Collapse,
} from "@mui/material";

import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";

import { useParams, useNavigate } from "react-router-dom";

import InnerImageZoom from "react-inner-image-zoom";
import "react-inner-image-zoom/lib/styles.min.css";

import { getProductById } from "../../utils/storage";

import CartToast from "../components/CartToast";

/* Catalogue record → the flat shape this page reads. */
const toView = (p) =>
    p && {
        id: p.id,
        title: p.name,
        brand: p.brand,
        stock: p.availability || "In stock",
        images: p.images || [],
    };

// ── Replace with your actual WhatsApp business number (with country code, no +) ──
const WHATSAPP_NUMBER = "918328890765";

export default function ProductDetails() {
    const { id } = useParams();
    const navigate = useNavigate();

    const product = toView(getProductById(id));

        useEffect(() => {

        document.title =
            "Product Details | Tecniqa";

    }, []);

    const [selectedImage, setSelectedImage] = useState(
        product?.images?.[0]
    );

    // ── Enquiry form state ──
    const [enquiryOpen, setEnquiryOpen] = useState(false);
    const [enquiryData, setEnquiryData] = useState({
        name: "",
        email: "",
        phone: "",
        quantity: "",
        message: "",
    });
    const [enquirySent, setEnquirySent] = useState(false);

    // ── Toast state ──
    const [toast, setToast] = useState({
        visible: false,
        message: "",
        type: "success",
    });

    const showToast = (message, type = "success") => {
        setToast({ visible: true, message, type });
        setTimeout(
            () => setToast((t) => ({ ...t, visible: false })),
            2800
        );
    };

    if (!product) {
        return <Typography>Product not found</Typography>;
    }

    // ── WhatsApp handler ──
    const handleWhatsApp = () => {
        const text = encodeURIComponent(
            `Hello! I'm interested in the product: *${product.title}* (Brand: ${product.brand}). Could you please share the pricing details?`
        );
        window.open(
            `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`,
            "_blank"
        );
    };

    // ── Enquiry submit handler ──
    const handleEnquirySubmit = () => {
        if (!enquiryData.name || !enquiryData.email || !enquiryData.phone) {
            showToast("Please fill in all required fields.", "warning");
            return;
        }
        // You can wire this to your backend / email API here
        console.log("Enquiry submitted:", {
            product: product.title,
            ...enquiryData,
        });
        setEnquirySent(true);
        showToast("Enquiry submitted successfully!", "success");
        setTimeout(() => {
            setEnquiryOpen(false);
            setEnquirySent(false);
            setEnquiryData({
                name: "",
                email: "",
                phone: "",
                quantity: "",
                message: "",
            });
        }, 2000);
    };

    return (
        <Box
            sx={{
                background:
                    "linear-gradient(to bottom, #f6f7f8, #eef1f3)",
                minHeight: "100vh",
                py: 3,
                px: 2,
            }}
        >
            <Box sx={{ maxWidth: 1180, mx: "auto" }}>

                {/* BACK BUTTON */}
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{
                        textTransform: "none",
                        borderRadius: "12px",
                        fontWeight: 600,
                        mb: 2.5,
                        color: "#1d2939",
                        borderColor: "#d0d5dd",
                        px: 2.2,
                        py: 0.8,
                        fontSize: "13px",
                        backgroundColor: "#fff",
                        "&:hover": {
                            borderColor: "#98a2b3",
                            backgroundColor: "#f8fafc",
                        },
                    }}
                >
                    Back
                </Button>

                {/* MAIN CARD */}
                <Card
                    sx={{
                        p: { xs: 2, md: 3 },
                        borderRadius: "28px",
                        background: "rgba(255,255,255,0.92)",
                        border: "1px solid rgba(226,232,240,0.9)",
                        boxShadow: "0 10px 35px rgba(15,23,42,0.06)",
                        backdropFilter: "blur(10px)",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            gap: 4,
                            alignItems: "flex-start",
                            flexDirection: { xs: "column", md: "row" },
                        }}
                    >
                        {/* LEFT IMAGE SECTION */}
                        <Box
                            sx={{
                                width: { xs: "100%", md: "42%" },
                                display: "flex",
                                gap: 1.5,
                            }}
                        >
                            {/* THUMBNAILS */}
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                }}
                            >
                                {product.images.map((img, index) => (
                                    <Box
                                        key={index}
                                        onMouseEnter={() =>
                                            setSelectedImage(img)
                                        }
                                        sx={{
                                            width: 62,
                                            height: 62,
                                            border:
                                                selectedImage === img
                                                    ? "2px solid #111827"
                                                    : "1px solid #d0d5dd",
                                            borderRadius: "14px",
                                            overflow: "hidden",
                                            cursor: "pointer",
                                            backgroundColor: "#fff",
                                            p: 0.5,
                                            transition: "0.3s ease",
                                            "&:hover": {
                                                borderColor: "#667085",
                                                transform:
                                                    "translateY(-2px)",
                                            },
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={img}
                                            alt=""
                                            sx={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "contain",
                                            }}
                                        />
                                    </Box>
                                ))}
                            </Box>

                            {/* MAIN IMAGE */}
                            <Box
                                sx={{
                                    flex: 1,
                                    border: "1px solid #e4e7ec",
                                    borderRadius: "24px",
                                    background:
                                        "linear-gradient(145deg,#ffffff,#f8fafc)",
                                    p: 2,
                                    minHeight: 390,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    overflow: "hidden",
                                }}
                            >
                                <InnerImageZoom
                                    src={selectedImage}
                                    zoomSrc={selectedImage}
                                    zoomType="hover"
                                    zoomScale={1.5}
                                    hideHint={true}
                                    width={360}
                                    height={360}
                                    imgAttributes={{
                                        style: {
                                            objectFit: "contain",
                                            width: "100%",
                                            height: "100%",
                                        },
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* RIGHT SIDE */}
                        <Box sx={{ flex: 1 }}>
                            {/* TITLE */}
                            <Typography
                                variant="h4"
                                fontWeight={800}
                                sx={{
                                    color: "#111827",
                                    lineHeight: 1.25,
                                    fontSize: { xs: "28px", md: "36px" },
                                }}
                            >
                                {product.title}
                            </Typography>

                            {/* BRAND + STOCK */}
                            <Stack
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                                sx={{ mt: 1.5, flexWrap: "wrap" }}
                            >
                                <Typography
                                    sx={{
                                        color: "#667085",
                                        fontSize: "14px",
                                    }}
                                >
                                    Brand :
                                    <Box
                                        component="span"
                                        sx={{
                                            color: "#111827",
                                            fontWeight: 700,
                                            ml: 0.7,
                                        }}
                                    >
                                        {product.brand}
                                    </Box>
                                </Typography>

                                <Chip
                                    label={product.stock}
                                    size="small"
                                    sx={{
                                        backgroundColor: "#ecfdf3",
                                        color: "#027a48",
                                        fontWeight: 700,
                                        borderRadius: "8px",
                                    }}
                                />
                            </Stack>

                            {/* ── PRICE REPLACED ── */}
                            <Box
                                sx={{
                                    mt: 2.5,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 1,
                                    background:
                                        "linear-gradient(135deg,#f8fafc,#f1f5f9)",
                                    border: "1px dashed #cbd5e1",
                                    borderRadius: "14px",
                                    px: 2.5,
                                    py: 1.2,
                                }}
                            >
                                <Typography
                                    fontWeight={700}
                                    sx={{
                                        color: "#475467",
                                        fontSize: "15px",
                                        letterSpacing: "0.3px",
                                    }}
                                >
                                    Price:
                                </Typography>
                                <Typography
                                    fontWeight={800}
                                    sx={{
                                        color: "#111827",
                                        fontSize: "17px",
                                        fontStyle: "italic",
                                    }}
                                >
                                    As per price
                                </Typography>
                            </Box>

                            {/* DESCRIPTION */}
                            <Typography
                                sx={{
                                    mt: 2.5,
                                    color: "#667085",
                                    lineHeight: 1.9,
                                    fontSize: "14px",
                                    maxWidth: "95%",
                                }}
                            >
                                Professional HVAC servicing equipment
                                engineered for high precision, durability,
                                and industrial performance. Ideal for
                                refrigeration and air-conditioning
                                maintenance applications.
                            </Typography>

                            {/* HIGHLIGHTS */}
                            <Box
                                sx={{
                                    mt: 3,
                                    background:
                                        "linear-gradient(135deg,#f8fafc,#f1f5f9)",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: "18px",
                                    p: 2,
                                }}
                            >
                                <Typography
                                    fontWeight={700}
                                    sx={{
                                        color: "#111827",
                                        mb: 1.5,
                                        fontSize: "16px",
                                    }}
                                >
                                    Highlights
                                </Typography>

                                <Grid container spacing={1}>
                                    {[
                                        "Heavy-duty build",
                                        "High precision",
                                        "Portable design",
                                        "Energy efficient",
                                    ].map((item, index) => (
                                        <Grid item xs={6} key={index}>
                                            <Typography
                                                sx={{
                                                    fontSize: "13px",
                                                    color: "#475467",
                                                }}
                                            >
                                                • {item}
                                            </Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>

                            {/* ── ACTION BUTTONS ── */}
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                sx={{ mt: 4 }}
                            >
                                {/* ORDER THROUGH WHATSAPP */}
                                <Button
                                    variant="contained"
                                    startIcon={<WhatsAppIcon />}
                                    onClick={handleWhatsApp}
                                    sx={{
                                        background:
                                            "linear-gradient(135deg,#25d366,#128c7e)",
                                        py: 1.2,
                                        px: 3,
                                        borderRadius: "14px",
                                        textTransform: "none",
                                        fontWeight: 700,
                                        fontSize: "14px",
                                        color: "#fff",

                                        "&:hover": {
                                            background:
                                                "linear-gradient(135deg,#20c45e,#0f7a6e)",

                                        },
                                    }}
                                >
                                    Order on WhatsApp
                                </Button>

                                {/* ENQUIRY ABOUT PRODUCT */}
                                <Button
                                    variant="contained"
                                    startIcon={<HelpOutlineOutlinedIcon />}
                                    onClick={() => setEnquiryOpen((v) => !v)}
                                    sx={{
                                        background: enquiryOpen
                                            ? "linear-gradient(135deg,#c89c22,#e9c454)"
                                            : "linear-gradient(135deg,#d4af37,#f5d76e)",
                                        py: 1.2,
                                        px: 3,
                                        borderRadius: "14px",
                                        textTransform: "none",
                                        fontWeight: 700,
                                        fontSize: "14px",
                                        color: "#111827",
                                        boxShadow:
                                            "0 10px 25px rgba(212,175,55,0.28)",
                                        "&:hover": {
                                            background:
                                                "linear-gradient(135deg,#c89c22,#e9c454)",
                                        },
                                    }}
                                >
                                    {enquiryOpen ? "Close Enquiry" : "Enquiry About Product"}
                                </Button>
                            </Stack>

                            {/* ── ENQUIRY DROPDOWN FORM ── */}
                            <Collapse in={enquiryOpen} timeout={350}>
                                <Box
                                    sx={{
                                        mt: 2.5,
                                        background:
                                            "linear-gradient(135deg,#fffbeb,#fefce8)",
                                        border: "1px solid #fde68a",
                                        borderRadius: "20px",
                                        p: 2.5,
                                        position: "relative",
                                    }}
                                >
                                    {/* Form header */}
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{ mb: 2 }}
                                    >
                                        <Typography
                                            fontWeight={700}
                                            sx={{
                                                color: "#111827",
                                                fontSize: "15px",
                                            }}
                                        >
                                            Product Enquiry
                                        </Typography>
                                        <Box
                                            onClick={() => setEnquiryOpen(false)}
                                            sx={{
                                                cursor: "pointer",
                                                color: "#667085",
                                                display: "flex",
                                                alignItems: "center",
                                                "&:hover": { color: "#111827" },
                                            }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </Box>
                                    </Stack>

                                    {enquirySent ? (
                                        <Typography
                                            sx={{
                                                textAlign: "center",
                                                color: "#027a48",
                                                fontWeight: 700,
                                                py: 2,
                                                fontSize: "15px",
                                            }}
                                        >
                                            ✅ Enquiry submitted! We'll get back to you soon.
                                        </Typography>
                                    ) : (
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    label="Full Name *"
                                                    size="small"
                                                    fullWidth
                                                    value={enquiryData.name}
                                                    onChange={(e) =>
                                                        setEnquiryData((d) => ({
                                                            ...d,
                                                            name: e.target.value,
                                                        }))
                                                    }
                                                    sx={inputSx}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    label="Email Address *"
                                                    size="small"
                                                    fullWidth
                                                    type="email"
                                                    value={enquiryData.email}
                                                    onChange={(e) =>
                                                        setEnquiryData((d) => ({
                                                            ...d,
                                                            email: e.target.value,
                                                        }))
                                                    }
                                                    sx={inputSx}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    label="Phone Number *"
                                                    size="small"
                                                    fullWidth
                                                    value={enquiryData.phone}
                                                    onChange={(e) =>
                                                        setEnquiryData((d) => ({
                                                            ...d,
                                                            phone: e.target.value,
                                                        }))
                                                    }
                                                    sx={inputSx}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    label="Quantity Required"
                                                    size="small"
                                                    fullWidth
                                                    type="number"
                                                    value={enquiryData.quantity}
                                                    onChange={(e) =>
                                                        setEnquiryData((d) => ({
                                                            ...d,
                                                            quantity: e.target.value,
                                                        }))
                                                    }
                                                    sx={inputSx}
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <TextField
                                                    label="Message / Requirements"
                                                    size="small"
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    value={enquiryData.message}
                                                    onChange={(e) =>
                                                        setEnquiryData((d) => ({
                                                            ...d,
                                                            message: e.target.value,
                                                        }))
                                                    }
                                                    sx={inputSx}
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Button
                                                    variant="contained"
                                                    endIcon={<SendIcon />}
                                                    fullWidth
                                                    onClick={handleEnquirySubmit}
                                                    sx={{
                                                        background:
                                                            "linear-gradient(135deg,#111827,#1f2937)",
                                                        py: 1.1,
                                                        borderRadius: "12px",
                                                        textTransform: "none",
                                                        fontWeight: 700,
                                                        fontSize: "14px",
                                                        boxShadow:
                                                            "0 8px 20px rgba(17,24,39,0.2)",
                                                        "&:hover": {
                                                            background:
                                                                "linear-gradient(135deg,#0f172a,#111827)",
                                                        },
                                                    }}
                                                >
                                                    Submit Enquiry
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    )}
                                </Box>
                            </Collapse>
                        </Box>
                    </Box>

                    {/* LOWER SECTION */}
                    <Divider sx={{ my: 4, borderColor: "#eaecf0" }} />

                    <Grid container spacing={3}>
                        {/* DESCRIPTION */}
                        <Grid item xs={12} md={7}>
                            <Typography
                                variant="h6"
                                fontWeight={700}
                                sx={{ color: "#111827", mb: 1.5 }}
                            >
                                Product Description
                            </Typography>

                            <Typography
                                sx={{
                                    color: "#667085",
                                    lineHeight: 1.9,
                                    fontSize: "14px",
                                }}
                            >
                                {product.title} is manufactured using advanced
                                HVAC servicing technology to ensure superior
                                efficiency, precision, and reliability. This
                                equipment is ideal for professional refrigeration
                                and air-conditioning servicing applications.
                            </Typography>
                        </Grid>

                        {/* SPECIFICATIONS */}
                        <Grid item xs={12} md={5}>
                            <Typography
                                variant="h6"
                                fontWeight={700}
                                sx={{ color: "#111827", mb: 1.5 }}
                            >
                                Specifications
                            </Typography>

                            <Grid container spacing={1.5}>
                                {[
                                    { label: "Brand", value: product.brand },
                                    {
                                        label: "Category",
                                        value: "HVAC Equipment",
                                    },
                                    {
                                        label: "Availability",
                                        value: product.stock,
                                    },
                                    { label: "Warranty", value: "1 Year" },
                                ].map((item, index) => (
                                    <Grid item xs={6} key={index}>
                                        <Box
                                            sx={{
                                                background: "#f8fafc",
                                                border: "1px solid #e4e7ec",
                                                borderRadius: "14px",
                                                p: 1.5,
                                                height: "100%",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    color: "#111827",
                                                    fontSize: "13px",
                                                    mb: 0.5,
                                                }}
                                            >
                                                {item.label}
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    color: "#667085",
                                                    fontSize: "13px",
                                                }}
                                            >
                                                {item.value}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>
                </Card>
            </Box>

            {/* CART TOAST */}
            <CartToast
                visible={toast.visible}
                message={toast.message}
                type={toast.type}
            />
        </Box>
    );
}

// ── Shared TextField styling ──
const inputSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "12px",
        backgroundColor: "#fff",
        fontSize: "13px",
        "& fieldset": { borderColor: "#e4e7ec" },
        "&:hover fieldset": { borderColor: "#667085" },
        "&.Mui-focused fieldset": { borderColor: "#111827" },
    },
    "& .MuiInputLabel-root": {
        fontSize: "13px",
        color: "#667085",
    },
    "& .MuiInputLabel-root.Mui-focused": {
        color: "#111827",
    },
};